import { downloadBookings } from '@/api/admin';
import { getAdminBookings, updateBooking } from '@/api/booking';
import BookingsTable from '@/components/admin/bookings';
import {
  Booking,
  BookingAdmin,
  BookingStatus,
  BookingStatusEnum,
  Consultant,
} from '@/types';
import { ROUTES } from '@/types/routes';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Flex, message, Modal, Typography } from 'antd';
import { format } from 'date-fns';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { getBookingStatusLabel } from '@/utils/status';

import { Link, useSearchParams } from 'react-router-dom';

export default function AdminBookings() {
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const queryClient = useQueryClient();

  // Modal State
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<BookingAdmin | null>(
    null,
  );

  const { data } = useQuery<
    {
      total: number;
      page: number;
      limit: number;
      sessions: BookingAdmin[];
    },
    Error
  >({
    queryKey: ['bookings', searchParams.toString(), page, limit],
    queryFn: () =>
      getAdminBookings({
        startDate: searchParams.get('startDate') || '',
        endDate: searchParams.get('endDate') || '',
        search: searchParams.get('search') || '',
        page,
        limit,
      }),
  });

  const { mutateAsync: downloadBookingsMutate, isPending } = useMutation({
    mutationFn: () =>
      downloadBookings({
        startDate: searchParams.get('startDate') || '',
        endDate: searchParams.get('endDate') || '',
        search: searchParams.get('search') || '',
      }),
    // Single source of truth for HTTP-failure toasts. handleDownloadTable
    // catches the rejection but does NOT toast again — see TCX-540, where
    // both this callback AND the catch block were firing message.error,
    // producing two identical toasts per failed click.
    onError: (error: any) => {
      console.error('downloadBookings failed:', error);
      message.error(
        error?.message || 'Bookings download failed. Please try again.',
      );
    },
  });

  const {
    mutateAsync: cancelBookingMutate,
    isPending: manualProcessingPending,
  } = useMutation({
    mutationFn: (record: BookingAdmin) =>
      updateBooking(record._id, {
        // dateTime: dayjs().toDate(),
        duration: record.duration,
        status: BookingStatusEnum.DECLINED,
      }),
    onSuccess: () => {
      message.success('Booking cancelled successfully');
      setIsCancelModalOpen(false);
      setSelectedBooking(null);
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
    onError: (error: any) => {
      message.error(error?.message || 'Failed to cancel booking');
    },
  });

  const handleOpenCancelModal = (record: BookingAdmin) => {
    setSelectedBooking(record);
    setIsCancelModalOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!selectedBooking) return;
    await cancelBookingMutate(selectedBooking);
  };

  const handleDownloadTable = async () => {
    let result: { file?: string };
    try {
      result = await downloadBookingsMutate();
    } catch {
      // Mutation rejected — onError already surfaced the toast. Bail silently
      // so we don't double-toast.
      return;
    }

    const base64Response = result?.file;
    if (!base64Response || typeof base64Response !== 'string') {
      message.error('Bookings download failed: server returned an invalid file.');
      return;
    }

    try {
      const csvContent = atob(base64Response);
      const blob = new Blob([csvContent], { type: 'text/csv' });

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `bookings_${new Date().toISOString()}.csv`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      message.success('File downloaded successfully');
    } catch (error: any) {
      console.error('Bookings CSV save failed:', error);
      message.error(error?.message || 'Failed to save file.');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: '_id',
      key: 'id',
      render: (id: string) => (
        <Link
          style={{ fontWeight: 500, color: '#1C8271' }}
          to={`${ROUTES.ADMIN_BOOKINGS}/${id}`}
        >
          {id}
        </Link>
      ),
    },
    {
      title: 'Franchise',
      dataIndex: 'franchise',
      key: 'franchise',
      render: (franchisor: { _id: string; company: string }) => (
        <Link
          style={{ fontWeight: 500, color: '#1C8271' }}
          to={`${ROUTES.ADMIN_FRANCHISES}/${franchisor._id}`}
        >
          {franchisor.company}
        </Link>
      ),
    },
    {
      title: 'Consultant',
      dataIndex: 'consultant',
      key: 'consultant',
      render: (consultant: {
        _id: string;
        firstName: string;
        lastName: string;
      }) => (
        <Link
          style={{ fontWeight: 500, color: '#1C8271' }}
          to={`${ROUTES.ADMIN_CONSULTANTS}/${consultant._id}`}
        >
          {consultant.firstName} {consultant.lastName}
        </Link>
      ),
    },
    {
      title: 'Meeting Booked',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Typography.Text>
            {date ? format(new Date(date), 'd MMM, yyyy, h:mm a') : 'N/A'}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: 'Meeting Start',
      dataIndex: 'start',
      key: 'start',
      render: (date: string, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Typography.Text>
            {date
              ? format(new Date(date), 'd MMM, yyyy, h:mm a')
              : record.createdAt
              ? format(new Date(record.createdAt), 'd MMM, yyyy, h:mm a')
              : 'N/A'}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: 'Marked As Completed',
      dataIndex: 'markedAsCompletedAt',
      key: 'markedAsCompletedAt',
      render: (date: string, record: BookingAdmin) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Typography.Text>
            {record.status === BookingStatus.COMPLETED && date
              ? format(new Date(date), 'd MMM, yyyy, h:mm a')
              : 'N/A'}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      render: (duration: number) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Typography.Text>{duration} min</Typography.Text>
        </div>
      ),
    },
    {
      title: 'Total ($)',
      dataIndex: 'consultant',
      key: 'totalAmount',
      render: (consultant: Consultant, record: Booking) =>
        record.totalAmount ? record.totalAmount : 0,
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: BookingAdmin) =>
        record.status === BookingStatus.PENDING ? (
          <Button
            danger
            size="small"
            onClick={() => handleOpenCancelModal(record)}
          >
            Cancel
          </Button>
        ) : null,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: 'pending' | 'declined' | 'completed' | 'future') => {
        const statusColors = {
          pending: '#FFA500',
          declined: '#F68B8B',
          completed: '#1C8271',
          future: '#2A4BC2',
        };
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: statusColors[status],
              }}
            />
            <Typography.Text>
              {getBookingStatusLabel(status)}
            </Typography.Text>
          </div>
        );
      },
    },
  ];

  return (
    <React.Fragment>
      <Flex justify={'space-between'} align="center" gap={24}>
        <Typography.Title
          level={2}
          style={{
            margin: '0 0 24px',
          }}
        >
          Booking Management
        </Typography.Title>
        {/* <Button
          type="primary"
          htmlType="submit"
          className="admin-button-download-table"
          onClick={handleDownloadTable}
          disabled={isPending}
          loading={isPending}
        >
          Download Table
        </Button> */}
      </Flex>
      <BookingsTable
        data={data?.sessions || []}
        total={data?.total || 0}
        columns={columns}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
        downloadData={handleDownloadTable}
        downloadLoading={isPending}
      />
      <Modal
        title="Cancel Booking"
        open={isCancelModalOpen}
        footer={null}
        centered
        onCancel={() => setIsCancelModalOpen(false)}
      >
        <div style={{ marginBlock: 16 }}>
          <Typography.Text>
            You are about to cancel this booking. This action will mark the
            session as declined and cannot be undone. Are you sure you want to
            continue?
          </Typography.Text>
        </div>

        <Flex gap={12}>
          <Button
            onClick={handleConfirmCancel}
            type="primary"
            danger
            style={{ width: '100%' }}
            loading={manualProcessingPending}
            disabled={manualProcessingPending}
          >
            Yes, Cancel Booking
          </Button>

          <Button
            type="default"
            onClick={() => setIsCancelModalOpen(false)}
            style={{ width: '100%' }}
            disabled={manualProcessingPending}
          >
            Keep Booking
          </Button>
        </Flex>
      </Modal>
    </React.Fragment>
  );
}
